import { Injectable, inject } from '@angular/core'
import { HttpOptionsFactory, UrlCollection } from '@dot-it/jml-lib'
import { HttpClient } from '@angular/common/http'
import { WizardNotification } from '../models/wizard-data'
import { Observable, ReplaySubject, Subject } from 'rxjs'
import { filter, tap } from 'rxjs/operators'
import { EntityReference } from '../../dto'
import { RxStompService } from '../../../app-config/rx-stomp.service'
import { GET_WIZARD_ACTIONS } from '../../../../../../shared/manager'
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog'
import { WizardComponent } from '../components/wizard.component'
import { DialogCleanupService } from '../../../services/dialog-cleanup.service'

@Injectable({
  providedIn: 'root'
})
export class WizardService {
  private http = inject(HttpClient)
  private rxStompService = inject(RxStompService)
  private dialogService = inject(DialogService)
  private dialogCleanupService = inject(DialogCleanupService)
  private wizardInstances = new Map<string, Subject<WizardNotification>>()

  constructor() {
    this.subscribeToWebSocketActions()
  }

  private subscribeToWebSocketActions() {
    this.rxStompService.watch(GET_WIZARD_ACTIONS()).subscribe((message) => {
      const payload = JSON.parse(message.body)

      if (payload && payload.id && this.wizardInstances.has(payload.id)) {
        const notification: WizardNotification = payload
        this.wizardInstances.get(payload.id).next(notification)
      }
    })
  }

  initWizard(wizardId: string, dtoType?: string, entityId?: number, fieldName?: string, context?: any) {
    // console.log('URL', UrlCollection.Wizard.INIT());
    const entityRef = !!dtoType && !!entityId ? new EntityReference(dtoType, entityId) : null
    const payload = {
      wizardId: wizardId,
      entityReference: entityRef,
      field: fieldName,
      context: context
    }

    const wizSubj = new ReplaySubject<WizardNotification>(1)
    this.http
      .post<WizardNotification>(
        UrlCollection.Wizard.INIT(),
        payload,
        new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
      )
      .subscribe((notification) => {
        this.wizardInstances.set(notification.id, wizSubj)
        wizSubj.next(notification)
      })
    return wizSubj.asObservable()
  }

  getWizard(executorId: string) {
    const subjectAlreadyExists = this.wizardInstances.has(executorId)
    const wizSubj = subjectAlreadyExists
      ? this.wizardInstances.get(executorId)
      : new ReplaySubject<WizardNotification>(1)
    if (!subjectAlreadyExists) {
      this.wizardInstances.set(executorId, wizSubj)
    }
    this.http
      .get<WizardNotification>(
        UrlCollection.Wizard.GET(executorId),
        new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
      )
      .subscribe((notification) => {
        wizSubj.next(notification)
      })
    return wizSubj.asObservable()
  }

  /**
   * if the push-update-flag is set for a field in the wizard, then updates will be sent to
   * BE to do some magic there
   */
  pushUpdate(wizardExecutorId: string, field: string, value: any, values: any) {
    this.http
      .post<WizardNotification>(
        UrlCollection.Wizard.UPDATE_FIELD(),
        { executorId: wizardExecutorId, field, value, values },
        new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
      )
      .subscribe((notification) => {
        this.wizardInstances.get(wizardExecutorId)?.next(notification)
      })
  }

  clickButton(wizardExecutorId: string, buttonId: string, values: any) {
    this.http
      .post<WizardNotification>(
        UrlCollection.Wizard.CLICK_BUTTON(),
        { executorId: wizardExecutorId, buttonId, values },
        new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
      )
      .subscribe((notification) => {
        this.wizardInstances.get(wizardExecutorId)?.next(notification)
      })
  }

  requestClose(wizardExecutorId: string, values: any) {
    this.http
      .post<WizardNotification>(
        UrlCollection.Wizard.CLOSE(),
        { executorId: wizardExecutorId, values },
        new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
      )
      .subscribe((notification) => {
        this.wizardInstances.get(wizardExecutorId)?.next(notification)
      })
  }

  selectEntity(wizardExecutorId: string, contextId: string, dtoType: string, entityId: number) {
    this.http
      .post<WizardNotification>(
        UrlCollection.Wizard.SELECT_ENTITY(),
        { executorId: wizardExecutorId, id: contextId, entityReference: { id: entityId, dtoType } },
        new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
      )
      .subscribe((notification) => {
        this.wizardInstances.get(wizardExecutorId)?.next(notification)
      })
  }

  openWizard(
    executorId?: string,
    wizardHrid?: string,
    dtoType?: string,
    entityId?: number,
    fieldName?: string,
    wizType?: any
  ) {
    const dialogRef = this.dialogService.open(WizardComponent, {
      data: { executorId, wizardHrid, dtoType, entityId, fieldName, wizType },
      header: 'Wizard',
      draggable: true,
      width: '500px',
      closeOnEscape: false,
      closable: false
    })

    this.dialogCleanupService.registerDialog(dialogRef)
  }
}
